import {
  AttributionControl,
  type GeoJSONSource,
  type LngLatBoundsLike,
  Map as MapLibreMap,
  Marker,
  setWorkerUrl,
} from "maplibre-gl"
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"

import {
  BAND,
  type GroundOffice,
  type GroundPoint,
  type GroundSubject,
} from "./store"
import { groundStyle, routeLayers } from "./style"

// MapLibre finds its worker from import.meta.url, which a bundler breaks: Vite
// serves the main module from its dependency cache, where the worker file is
// not. `?worker&url` has Vite bundle the worker (and the shared chunk it
// imports) as one self-contained file and hand back its URL.
setWorkerUrl(workerUrl)

/*
 * MOTION — the ground resolves (Master Design System §12; the motion prompt).
 *
 * Navigating index → show is not a page swap. The ground is one persistent
 * element; passing it a mappable point causes it to resolve — the blur
 * dissolves into particulars, like a lens pulling focus. The leaf and the map
 * move on independent tracks, total ≤ 700ms, nothing bounces:
 *
 *   1  the leaf departs downward   24px · 180ms ease-in (the layout)
 *   2  the ground resolves         the live map mounts UNDER the still, already
 *                                  near the job; the still is the loading state
 *                                  and fades (450ms) once tiles are idle or
 *                                  350ms passes — never before 120ms, never a
 *                                  spinner, never a blur over a live map
 *   3  the camera settles the pin  600ms ease-out into the clear band, via
 *                                  padding — never by fudging the centre; the
 *                                  pin and chip scale .85→1 at ~80% of settle
 *   4  the show leaf arrives       rises 24px · 240ms ease-out from ~200ms
 *
 * Retreat is quicker than arrival (~400ms): the still fades back in while the
 * camera exhales ~8% outward, and the map unmounts silently under it.
 * Reduced motion: 150ms crossfades, jumpTo, no travel.
 */
export const MOTION = {
  leafOut: 180,
  leafIn: 240,
  leafInDelay: 200,
  leafReturnDelay: 120,
  stillOut: 450,
  camera: 600,
  pinAt: 480,
  stillIn: 300,
  exhale: 320,
  unmountAt: 450,
  idleCap: 350,
  minWait: 120,
  dock: 380,
  expand: 450,
  panelIn: 240,
  panelOut: 160,
  mark: 200,
  officeAt: 520,
  fit: 700,
  crossfade: 150,
} as const

const EASE_OUT_CUBIC = (t: number) => 1 - Math.pow(1 - t, 3)
const EASE_OUT_QUAD = (t: number) => 1 - Math.pow(1 - t, 2)
const CSS_EASE_OUT = "cubic-bezier(0,0,.2,1)"
const CSS_EASE_STANDARD = "cubic-bezier(.4,0,.2,1)"

export const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

/** The frame the pin settles into: centred in the clear band above the sheet. */
const CHROME_TOP = 24
const BAND_FRAME_BOTTOM = BAND + CHROME_TOP
const SIDE = 40
const ZOOM_ARRIVE = 13.2
const NO_PADDING = { top: 0, right: 0, bottom: 0, left: 0 }
const ZOOM_REST = 14.4

const samePoint = (a: GroundPoint, b: GroundPoint) =>
  a.lng === b.lng && a.lat === b.lat

const lngLat = (p: GroundPoint): [number, number] => [p.lng, p.lat]

/** A marker element that arrives by scaling in from .85; hidden until asked. */
const mark = (html: string) => {
  const wrapper = document.createElement("div")
  wrapper.innerHTML = html
  const el = wrapper.firstElementChild as HTMLElement
  el.style.opacity = "0"
  el.style.transform = "scale(.85)"
  return el
}

const show = (el: HTMLElement | null, instant: boolean) => {
  if (!el) return
  el.style.transition = instant
    ? "none"
    : `opacity ${MOTION.mark}ms ${CSS_EASE_OUT}, transform ${MOTION.mark}ms ${CSS_EASE_OUT}`
  el.style.opacity = "1"
  el.style.transform = "scale(1)"
}

const hide = (el: HTMLElement | null) => {
  if (!el) return
  el.style.opacity = "0"
  el.style.transform = "scale(.85)"
}

/**
 * The imperative half of the ground: owns the MapLibre map, the markers, the
 * still's fade and every timer. React tells it what the page wants; it never
 * renders. One engine lives for as long as the ground layer is on screen.
 */
export class GroundEngine {
  private map: MapLibreMap | null = null
  private markers: Marker[] = []
  private pinEl: HTMLElement | null = null
  private chipEl: HTMLElement | null = null
  private officeMarker: Marker | null = null
  private officeEl: HTMLElement | null = null
  private subject: GroundSubject | null = null
  private timers: ReturnType<typeof setTimeout>[] = []

  constructor(
    private readonly mapEl: HTMLElement,
    private readonly stillEl: HTMLElement,
    private readonly onTerritoryClick: () => void,
  ) {}

  destroy() {
    this.clearTimers()
    this.unmount()
  }

  /* --- the two states ---------------------------------------------------- */

  /** An index: the still, opaque. From a subject page, the retreat. */
  retreat() {
    this.clearTimers()
    const still = this.stillEl
    still.style.pointerEvents = "auto"
    this.subject = null

    if (!this.map) {
      still.style.transition = "none"
      still.style.opacity = "1"
      return
    }

    if (reducedMotion()) {
      still.style.transition = `opacity ${MOTION.crossfade}ms linear`
      still.style.opacity = "1"
      this.later(() => this.unmount(), MOTION.crossfade + 50)
      return
    }

    // retreat is quicker than arrival: the still fades IN over the live map
    // while the camera exhales ~8% outward — a breath, not a flight
    still.style.transition = `opacity ${MOTION.stillIn}ms ${CSS_EASE_STANDARD}`
    still.style.opacity = "1"
    this.map.easeTo({
      zoom: this.map.getZoom() - 0.11,
      duration: MOTION.exhale,
      easing: EASE_OUT_QUAD,
    })
    this.later(() => this.unmount(), MOTION.unmountAt) // silently, under the opaque still
  }

  /** A subject page: mount the live map under the still and let the ground resolve onto the point. */
  resolveOnto(subject: GroundSubject, apiKey: string | null) {
    if (
      this.map &&
      this.subject &&
      samePoint(this.subject.point, subject.point)
    ) {
      this.subject = subject
      return
    }

    if (this.map) {
      // another job while the territory is already showing: re-frame, no fog;
      // the drive follows the job
      this.subject = subject
      this.placeMarkers(subject)
      this.setRoute(subject.office)
      this.camera(this.bandFrame(), MOTION.camera)
      show(this.pinEl, true)
      show(this.chipEl, true)
      return
    }

    this.clearTimers()
    this.subject = subject
    const t0 = performance.now()
    const map = this.mount(subject, apiKey)
    if (!map) return // no key, no WebGL: the still IS the loading state, and it stays — no spinner, ever

    // the still stays on top until tiles are idle OR 350ms passes — whichever
    // first, and never before 120ms
    let started = false
    const start = () => {
      if (started || this.subject !== subject) return
      started = true
      const wait = Math.max(0, MOTION.minWait - (performance.now() - t0))
      this.later(() => this.resolve(), wait)
    }
    map.once("idle", start)
    this.later(start, MOTION.idleCap)
  }

  private resolve() {
    const still = this.stillEl
    if (!this.map) return
    still.style.pointerEvents = "none" // once the fog lifts, pan and click reach the territory

    if (reducedMotion()) {
      this.map.jumpTo(this.bandFrame())
      still.style.transition = `opacity ${MOTION.crossfade}ms linear`
      still.style.opacity = "0"
      show(this.pinEl, true)
      show(this.chipEl, true)
      return
    }

    still.style.transition = `opacity ${MOTION.stillOut}ms ${CSS_EASE_STANDARD}`
    still.style.opacity = "0"
    this.camera(this.bandFrame(), MOTION.camera)
    this.later(() => {
      show(this.pinEl, false)
      show(this.chipEl, false)
    }, MOTION.pinAt) // ~80% of settle
  }

  /* --- the sheet's three modes ------------------------------------------- */

  /** The territory is clickable: the sheet docks and the camera re-pads to the whole viewport. */
  expand() {
    if (!this.map) return
    this.camera(this.expandedFrame(), MOTION.expand)
    this.map.scrollZoom.enable()
  }

  /** Directions start at your office: frame office → job around the dashed drive, then land the office mark. */
  directions(office: GroundOffice | null) {
    if (!this.map || !this.subject) return
    this.map.scrollZoom.enable()
    if (!office) {
      this.expand()
      return
    }
    this.setRoute(office) // the job's office, or the one chosen for this visit
    const { point } = this.subject
    const bounds: LngLatBoundsLike = [
      [Math.min(office.lng, point.lng), Math.min(office.lat, point.lat)],
      [Math.max(office.lng, point.lng), Math.max(office.lat, point.lat)],
    ]
    // the camera still carries the band frame's padding, which would shrink
    // the box the drive has to fit — so frame the bounds against a clean
    // slate. cameraForBounds bakes the padding it is given into the centre
    // and zoom it returns (fitBounds drops the padding before easing for the
    // same reason), so the eased frame carries none, or it shifts by the
    // difference between the panel's side and the other
    const padding = { top: 110, bottom: 130, left: 360, right: 90 }
    const held = this.map.getPadding()
    this.map.setPadding(NO_PADDING)
    const camera = this.map.cameraForBounds(bounds, { padding })
    this.map.setPadding(held)
    if (camera) {
      const frame = {
        center: camera.center,
        zoom: camera.zoom,
        padding: NO_PADDING,
      }
      if (reducedMotion()) this.map.jumpTo(frame)
      else
        this.map.easeTo({
          ...frame,
          duration: MOTION.fit,
          easing: EASE_OUT_CUBIC,
        })
    }
    if (this.map.getLayer("route")) {
      this.map.setPaintProperty("route", "line-width", 2.4)
    }
    this.later(
      () => show(this.officeEl, reducedMotion()),
      reducedMotion() ? 0 : MOTION.officeAt,
    )
  }

  /** The sheet comes back: the office mark leaves, the camera settles the pin into the band again. */
  restore() {
    if (!this.map) return
    hide(this.officeEl)
    if (this.map.getLayer("route")) {
      this.map.setPaintProperty("route", "line-width", 1.8)
    }
    this.camera(this.bandFrame(), MOTION.camera)
    this.map.scrollZoom.disable()
  }

  /**
   * The drive the map draws — office → job, dashed — and the office mark at
   * its start. Called with the job's own office, or with the one chosen for
   * the visit; with none, the line is empty and the mark leaves. The mark is
   * moved, never re-made, so a change of origin while directions are open
   * does not blink it out.
   */
  setRoute(office: GroundOffice | null) {
    const map = this.map
    if (!map || !this.subject) return
    const data = routeData(office, this.subject.point)
    const apply = () => {
      const source = this.map?.getSource("route") as GeoJSONSource | undefined
      source?.setData(data)
    }
    if (map.getSource("route")) apply()
    else map.once("load", apply) // the style is still arriving: the source lands with it
    this.placeOffice(office)
  }

  /** The address is a mappable point: re-centre on it in whatever frame the ground currently holds. */
  recenter(docked: boolean) {
    if (!this.map) return
    this.camera(docked ? this.expandedFrame() : this.bandFrame(), MOTION.camera)
  }

  /* --- frames ------------------------------------------------------------ */

  private bandFrame() {
    const h = this.mapEl.clientHeight || window.innerHeight
    // the pin lands centred in the clear band via padding — never by fudging the centre
    return {
      center: lngLat(this.subject!.point),
      zoom: ZOOM_REST,
      padding: {
        top: CHROME_TOP,
        bottom: Math.max(h - BAND_FRAME_BOTTOM, 0),
        left: SIDE,
        right: SIDE,
      },
    }
  }

  private expandedFrame() {
    return {
      center: lngLat(this.subject!.point),
      zoom: ZOOM_REST,
      padding: { top: CHROME_TOP, bottom: 96, left: SIDE, right: SIDE },
    }
  }

  private camera(
    frame: ReturnType<GroundEngine["bandFrame"]>,
    duration: number,
  ) {
    if (!this.map) return
    if (reducedMotion()) this.map.jumpTo(frame)
    else this.map.easeTo({ ...frame, duration, easing: EASE_OUT_CUBIC })
  }

  /* --- the map ----------------------------------------------------------- */

  /** Mounts the live map under the still; null when there is no key or no WebGL. */
  private mount(subject: GroundSubject, apiKey: string | null) {
    if (this.map) return this.map
    if (!apiKey) {
      if (import.meta.env.DEV)
        console.info("[ground] no Stadia key: the still stays")
      return null
    }

    // the drive is always a source, empty when there is no office, so a later
    // job or a chosen origin can draw one without touching the style
    const style = groundStyle(
      apiKey,
      {
        route: {
          type: "geojson",
          data: routeData(subject.office, subject.point),
        },
      },
      routeLayers(),
    )

    let map: MapLibreMap
    try {
      // mounts already NEAR the destination: zoom ~13.2, centre offset slightly south
      map = new MapLibreMap({
        container: this.mapEl,
        style,
        center: [subject.point.lng, subject.point.lat - 0.006],
        zoom: ZOOM_ARRIVE,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        scrollZoom: false,
      })
    } catch (error) {
      // no WebGL, or MapLibre refusing the container: the still stays, and in
      // development the reason is at least said
      if (import.meta.env.DEV)
        console.warn("[ground] the map could not mount", error)
      return null
    }

    // the attribution sits at the band's bottom-right, where the ground is
    // always in view — the map's own bottom is under the sheet, docked or not
    map.addControl(new AttributionControl({ compact: true }), "top-right")
    map.on("error", (event) => {
      // a tile that fails to load leaves paper where it would have been, and
      // the still is already the fallback — so nothing is shown; in development
      // the reason is at least said
      if (import.meta.env.DEV) console.warn("[ground] map error", event.error)
    })
    map.on("click", (event) => {
      const target = event.originalEvent.target as HTMLElement | null
      if (target?.closest(".maplibregl-marker")) return
      this.onTerritoryClick()
    })
    this.map = map
    this.placeMarkers(subject)
    this.placeOffice(subject.office)
    return map
  }

  private placeMarkers(subject: GroundSubject) {
    if (!this.map) return
    this.markers.forEach((m) => m.remove())
    this.markers = []

    const at = lngLat(subject.point)
    this.chipEl = mark(
      `<span class="map-label">${escapeHtml(subject.label)}</span>`,
    )
    this.chipEl.style.transformOrigin = "50% 100%"
    this.pinEl = mark(
      `<span class="map-pin"><span class="map-pin-dot"></span></span>`,
    )
    this.markers.push(
      new Marker({
        element: wrap(this.chipEl),
        anchor: "bottom",
        offset: [0, -14],
      })
        .setLngLat(at)
        .addTo(this.map),
      new Marker({ element: wrap(this.pinEl), anchor: "center" })
        .setLngLat(at)
        .addTo(this.map),
    )
  }

  /** The office mark: print.900 on the board's material, hidden until directions. Moved when it already exists. */
  private placeOffice(office: GroundOffice | null) {
    if (!this.map) return
    if (!office) {
      this.officeMarker?.remove()
      this.officeMarker = this.officeEl = null
      return
    }
    if (this.officeMarker && this.officeEl) {
      this.officeMarker.setLngLat(lngLat(office))
      const label = this.officeEl.querySelector(".map-label")
      if (label) label.textContent = office.label
      return
    }
    this.officeEl = mark(
      `<span class="flex flex-col items-center gap-1"><span class="map-office"></span><span class="map-label text-[10.5px]">${escapeHtml(office.label)}</span></span>`,
    )
    this.officeEl.style.transformOrigin = "50% 8px"
    this.officeMarker = new Marker({
      element: wrap(this.officeEl),
      anchor: "top",
      offset: [0, -7],
    })
      .setLngLat(lngLat(office))
      .addTo(this.map)
  }

  private unmount() {
    this.markers.forEach((m) => m.remove())
    this.markers = []
    this.officeMarker?.remove()
    this.officeMarker = null
    this.pinEl = this.chipEl = this.officeEl = null
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  private later(fn: () => void, ms: number) {
    this.timers.push(setTimeout(fn, ms))
  }

  private clearTimers() {
    this.timers.forEach(clearTimeout)
    this.timers = []
  }
}

/** The drive as GeoJSON: one dashed line office → job, or nothing at all. */
const routeData = (
  office: GroundOffice | null,
  point: GroundPoint,
): GeoJSON.GeoJSON =>
  office
    ? {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [lngLat(office), lngLat(point)],
        },
      }
    : { type: "FeatureCollection", features: [] }

/** MapLibre positions the marker element itself; the animated child sits inside it. */
const wrap = (el: HTMLElement) => {
  const outer = document.createElement("div")
  outer.appendChild(el)
  return outer
}

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ] ?? c,
  )
